import { createPortal } from "react-dom";
import type { WorkflowSelectedValues } from "./choiceModel";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type WheelEvent } from "react";
import { useMsal } from "@azure/msal-react";
import { AlertCircle, Check, CheckCircle, ChevronLeft, Edit2, MoreHorizontal, RotateCcw, Save, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";

import { getToken, useMsalAuth } from "../../authConfig";
import { useProjects } from "../../contexts/ProjectContext";
import { getNoteApi } from "../../api/api";
import type { Note, NoteContent, Project } from "../../api/models";
import { Button, Menu, Modal } from "../../components/ui";
import { AnswerError, AnswerLoading } from "../../components/Answer";
import { InteractiveNoteContent } from "../../components/InteractiveNoteContent";
import { QuestionInput } from "../../components/QuestionInput";

import { UserChatMessage } from "../../components/UserChatMessage";
import {
    approveDocumentationDraft,
    cancelDocumentationDraft,
    createDocumentationGroup,
    getDocumentationDraft,
    getDocumentationGroup,
    processDocumentationDraft,
    replaceDocumentationWorkflow,

    updateDocumentationFields,
    updateDocumentationWorkflowNodes
} from "./api";
import { replaceDocumentationCardWorkflow, saveDocumentationCard, submitDocumentationCard } from "./api";
import { applyDocumentationDraft, mergeDocumentationDrafts, runDocumentationCardAction } from "./cardActionSync";
import { processingFailure } from "./processingFailure";
import { WorkflowCardsReview } from "./WorkflowCardsReview";
import { DocumentationCardEditor } from "./DocumentationCardEditor";
import { DocumentationNoteDateTime, stockholmDateTime } from "./DocumentationNoteView";
import { shouldRenderConversationBeforeCards } from "./documentationConversationLayout";
import {
    DOCUMENTATION_PROCESS_TIMEOUT_MS,
    isDocumentationDraftProcessing,
    pollDocumentationDraftUntilTerminal,
    shouldPollDocumentationDraft
} from "./documentationProcessing";
import { WorkflowInstanceSwitcher } from "./WorkflowInstanceSwitcher";
import { WorkflowNodeResults } from "./WorkflowNodeResults";
import { DocumentationApiError, DocumentationDraft, DocumentationGroup, DocumentationValidationIssue } from "./models";
import { buildWorkflowNodeChanges, WorkflowNodeEdits } from "./workflowNodeResultUtils";

import flow from "../../components/DocumentFlow/DocumentFlow.module.css";
import styles from "./DocumentationWorkspace.module.css";

type Props = {
    onDismiss: () => void;
    projectId?: string;
    autoStartVoiceToken?: number;
    initialMessage?: string;
    embedded?: boolean;
    composerTarget?: HTMLElement | null;
    routedWorkflowId?: string;
};

type DocumentFlowStage = "idle" | "selectProject" | "askWhat" | "generating" | "noteReady" | "error";
type FailedAction = "loadProjects" | "generateNote" | null;
type DraftAction = "save" | "retry" | "cancel" | "revise";
type EditSnapshot = {
    draftId: string;
    workflowId: string;
    fieldEdits: Record<string, unknown>;
    fieldConfirmations: Set<string>;
    nodeEdits: WorkflowNodeEdits;
    selectedValues: WorkflowSelectedValues;
    nodeConfirmations: Set<string>;
};

type DraftFieldEdits = Record<string, Record<string, unknown>>;
type DraftNodeEdits = Record<string, WorkflowNodeEdits>;
type DraftConfirmations = Record<string, Set<string>>;
type DraftWorkflowSelections = Record<string, string>;

const PENDING_CREATE_KEY = "documentation.pendingCreate";

function latestDraft(events: Array<{ draft?: DocumentationDraft }>): DocumentationDraft | undefined {
    return [...events].reverse().find(event => event.draft)?.draft;
}

function sourceNoteContentText(content: string | NoteContent): string {
    if (typeof content === "string") return content;

    return [
        content.date && `Date: ${content.date}`,
        content.time && `Time: ${content.time}`,
        content.workflow && `Workflow: ${content.workflow}`,
        content.ibic && `IBIC: ${content.ibic}`,
        content.ibic_code && `IBIC Code: ${content.ibic_code}`,
        content.deviation && `Deviation: ${content.deviation}`,
        content.note_type && `Note type: ${content.note_type}`,
        content.text,
        content.assessment && `Assessment: ${content.assessment}`
    ]
        .filter((line): line is string => Boolean(line))
        .join("\n");
}

function documentationProcessTimeoutError(): DocumentationApiError {
    return new DocumentationApiError(
        "DOCUMENTATION_PROCESS_TIMEOUT",
        "The documentation is still processing. Please try again.",
        504
    );
}

function abortProcessingControllers(controllers: Set<AbortController>, reason?: unknown): void {
    controllers.forEach(controller => (reason === undefined ? controller.abort() : controller.abort(reason)));
    controllers.clear();
}

async function runWithProcessingController<T>(controllers: Set<AbortController>, run: (signal: AbortSignal, onActivity: () => void) => Promise<T>): Promise<T> {
    const controller = new AbortController();
    controllers.add(controller);
    let timeout = globalThis.setTimeout(() => controller.abort(documentationProcessTimeoutError()), DOCUMENTATION_PROCESS_TIMEOUT_MS);
    const onActivity = () => {
        globalThis.clearTimeout(timeout);
        timeout = globalThis.setTimeout(() => controller.abort(documentationProcessTimeoutError()), DOCUMENTATION_PROCESS_TIMEOUT_MS);
    };
    try {
        return await run(controller.signal, onActivity);
    } catch (error) {
        if (controller.signal.reason instanceof DocumentationApiError && controller.signal.reason.code === "DOCUMENTATION_PROCESS_TIMEOUT") {
            throw controller.signal.reason;
        }
        throw error;
    } finally {
        globalThis.clearTimeout(timeout);
        controllers.delete(controller);
    }
}

function isAbortError(error: unknown): boolean {
    return error instanceof Error && error.name === "AbortError";
}

export const DocumentationWorkspace = ({ onDismiss, projectId, autoStartVoiceToken, initialMessage, routedWorkflowId, embedded = false, composerTarget }: Props) => {
    const { t, i18n } = useTranslation();
    const client = useMsalAuth ? useMsal().instance : undefined;
    const { projects, loadProjects } = useProjects();
    const shouldLoadProjects = useRef(projects.length === 0);

    const [stage, setStage] = useState<DocumentFlowStage>("idle");
    const [selectedProjects, setSelectedProjects] = useState<Project[]>([]);
    const [group, setGroup] = useState<DocumentationGroup | null>(null);
    const [drafts, setDrafts] = useState<DocumentationDraft[]>([]);
    const [fieldEdits, setFieldEdits] = useState<DraftFieldEdits>({});
    const [fieldConfirmations, setFieldConfirmations] = useState<DraftConfirmations>({});
    const [nodeEdits, setNodeEdits] = useState<DraftNodeEdits>({});
    const [selectedValues, setSelectedValues] = useState<Record<string, WorkflowSelectedValues>>({});
    const [nodeConfirmations, setNodeConfirmations] = useState<DraftConfirmations>({});
    const [workflowSelections, setWorkflowSelections] = useState<DraftWorkflowSelections>({});
    const [busyDrafts, setBusyDrafts] = useState<Record<string, DraftAction>>({});
    const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
    const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
    const [editSnapshot, setEditSnapshot] = useState<EditSnapshot | null>(null);

    const [dirtyCards, setDirtyCards] = useState<Record<string, boolean>>({});
    const hasUnsavedEdits = Object.values(dirtyCards).some(Boolean) || editingDraftId !== null;
    const blocker = useBlocker(hasUnsavedEdits);
    useEffect(() => {
        if (blocker.state !== "blocked") return;
        if (window.confirm(t("documentFlow.leaveUnsaved", { defaultValue: "Leave without saving? Unsaved edits will be discarded." }))) blocker.proceed();
        else blocker.reset();
    }, [blocker, t]);
    useEffect(() => {
        if (!hasUnsavedEdits) return;
        const protect = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
        window.addEventListener("beforeunload", protect);
        return () => window.removeEventListener("beforeunload", protect);
    }, [hasUnsavedEdits]);
    const handleCardDirty = useCallback((draftId: string, dirty: boolean) => {
        setDirtyCards(current => (current[draftId] === dirty ? current : { ...current, [draftId]: dirty }));
    }, []);
    // Draft id awaiting the ⋯-menu discard confirmation.
    const [discardTarget, setDiscardTarget] = useState<string | null>(null);
    const [sourceNoteTarget, setSourceNoteTarget] = useState<{ projectId: string; noteId: string } | null>(null);
    const [sourceNote, setSourceNote] = useState<Note | null>(null);
    const [sourceNoteError, setSourceNoteError] = useState<string | null>(null);
    const sourceNoteRequestRef = useRef(0);
    const [submittedText, setSubmittedText] = useState<string>("");
    const [pendingConversationMessage, setPendingConversationMessage] = useState<string | null>(null);
    const [error, setError] = useState<unknown>();
    const [lastFailedAction, setLastFailedAction] = useState<FailedAction>(null);
    const [pendingVoiceToken, setPendingVoiceToken] = useState<number | undefined>(autoStartVoiceToken);
    const projectChipsRowRef = useRef<HTMLDivElement | null>(null);
    const createInFlightRef = useRef(false);
    const homeMessageStarted = useRef(false);
    const processingControllersRef = useRef<Set<AbortController>>(new Set());
    const mountedRef = useRef(true);
    const flowGenerationRef = useRef(0);
    const cardRequestsRef = useRef(new Map<string, symbol>());
    const activeGroupIdRef = useRef(group?.id ?? null);
    activeGroupIdRef.current = group?.id ?? null;

    const stageRef = useRef<DocumentFlowStage>(stage);
    stageRef.current = stage;

    const resolveToken = useCallback(async (): Promise<string | undefined> => {
        if (!client) return "";
        return getToken(client);
    }, [client]);

    const openSourceNote = useCallback(
        async (sourceProjectId: string, noteId: string) => {
            const requestId = sourceNoteRequestRef.current + 1;
            sourceNoteRequestRef.current = requestId;
            setSourceNoteTarget({ projectId: sourceProjectId, noteId });
            setSourceNote(null);
            setSourceNoteError(null);

            try {
                const note = await getNoteApi(sourceProjectId, noteId, await resolveToken());
                if (sourceNoteRequestRef.current === requestId) setSourceNote(note);
            } catch (requestError) {
                if (sourceNoteRequestRef.current !== requestId) return;
                setSourceNoteError(requestError instanceof Error ? requestError.message : String(requestError));
            }
        },
        [resolveToken]
    );

    const closeSourceNote = useCallback(() => {
        sourceNoteRequestRef.current += 1;
        setSourceNoteTarget(null);
        setSourceNote(null);
        setSourceNoteError(null);
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            flowGenerationRef.current += 1;
            cardRequestsRef.current.clear();
            sourceNoteRequestRef.current += 1;
            abortProcessingControllers(processingControllersRef.current);
        };
    }, []);

    const refreshGroup = useCallback(
        async (groupId: string) => {
            const generation = flowGenerationRef.current;
            const response = await getDocumentationGroup(groupId, await resolveToken());
            if (mountedRef.current && generation === flowGenerationRef.current && activeGroupIdRef.current === groupId) {
                setGroup(response.group);
                setDrafts(current => mergeDocumentationDrafts(current, response.drafts));
            }
            return response;
        },
        [resolveToken]
    );

    useEffect(() => {
        if (autoStartVoiceToken !== undefined) setPendingVoiceToken(autoStartVoiceToken);
    }, [autoStartVoiceToken]);

    useEffect(() => {
        let cancelled = false;

        const bootstrap = async () => {
            try {
                if (shouldLoadProjects.current) {
                    shouldLoadProjects.current = false;
                    await loadProjects();
                }
                if (cancelled) return;

                // Each entry starts fresh; do not load previous drafts after project selection.

                // Only the initial stages are (re)derived; once the user has moved on, the flow owns the stage.
                if (stageRef.current !== "idle" && stageRef.current !== "selectProject") return;

                if (projectId) {
                    const match = projects.find(project => project.id === projectId);
                    if (match) {
                        setSelectedProjects([match]);
                        setStage("askWhat");
                        return;
                    }
                }
                setStage("selectProject");
            } catch (loadError) {
                if (!cancelled) {
                    setError(loadError);
                    setLastFailedAction("loadProjects");
                    setStage("error");
                }
            }
        };

        void bootstrap();

        return () => {
            cancelled = true;
        };
    }, [loadProjects, projectId, projects, resolveToken]);

    const availableProjects = useMemo(() => [...projects].sort((left, right) => left.name.localeCompare(right.name, i18n.language)), [i18n.language, projects]);
    const projectNames = useMemo(() => new Map(projects.map(project => [project.id, project.name])), [projects]);
    const selectedProjectNames = useMemo(() => selectedProjects.map(project => project.name).join(", "), [selectedProjects]);
    const isBusy = Object.keys(busyDrafts).length > 0;

    // Whole-draft regeneration cannot rewrite a card already handed off.
    const revisableDrafts = useMemo(
        () =>
            drafts.filter(
                draft => (draft.submitted_card_ids?.length ?? 0) === 0 && (
                    draft.status === "created" ||
                    draft.status === "processing_failed" ||
                    draft.status === "ready_for_review" ||
                    draft.status === "pending_signature")
            ),
        [drafts]
    );

    // Every revisable draft in the group gets the same request, so the longest list
    // is the full history — a draft signed midway simply stops collecting.
    const conversationTurns = useMemo(
        () =>
            drafts.reduce<NonNullable<DocumentationDraft["conversation_turns"]>>((longest, draft) => {
                const turns = draft.conversation_turns?.length
                    ? draft.conversation_turns
                    : (draft.input.follow_ups ?? []).map(content => ({
                          role: "user" as const,
                          content,
                          kind: "revision" as const,
                          created_at: ""
                      }));
                return turns.length > longest.length ? turns : longest;
            }, []),
        [drafts]
    );

    const updateDraft = useCallback((updated: DocumentationDraft) => {
        setDrafts(current => applyDocumentationDraft(current, updated));
    }, []);

    const runCardAction = useCallback(
        async (draft: DocumentationDraft, write: (token: string | undefined) => Promise<DocumentationDraft>) => {
            const generation = flowGenerationRef.current;
            const requestId = Symbol(draft.id);
            cardRequestsRef.current.set(draft.id, requestId);
            const isCurrent = () =>
                mountedRef.current &&
                generation === flowGenerationRef.current &&
                activeGroupIdRef.current === draft.documentation_group_id &&
                cardRequestsRef.current.get(draft.id) === requestId;
            try {
                if (!isCurrent()) return;
                const tokenPromise = resolveToken();
                await runDocumentationCardAction({
                    write: async () => {
                        const token = await tokenPromise;
                        if (!isCurrent()) throw new DOMException("The documentation operation was cancelled.", "AbortError");
                        return write(token);
                    },
                    recover: async () => getDocumentationDraft(draft.project_id, draft.id, await tokenPromise),
                    apply: updateDraft,
                    isCurrent,
                    setBusy: busy => {
                        setBusyDrafts(current => {
                            if (busy) return { ...current, [draft.id]: "save" };
                            const next = { ...current };
                            delete next[draft.id];
                            return next;
                        });
                    }
                });
            } finally {
                if (cardRequestsRef.current.get(draft.id) === requestId) cardRequestsRef.current.delete(draft.id);
            }
        },
        [resolveToken, updateDraft]
    );

    const withBusyDraft = useCallback(
        async (draftId: string, action: DraftAction, run: () => Promise<void>) => {
            setBusyDrafts(current => ({ ...current, [draftId]: action }));
            setDraftErrors(current => {
                const next = { ...current };
                delete next[draftId];
                return next;
            });
            try {
                await run();
            } catch (actionError) {
                if (isAbortError(actionError)) return;
                const message = actionError instanceof Error ? actionError.message : String(actionError);
                setDraftErrors(current => ({ ...current, [draftId]: message }));
                if (actionError instanceof DocumentationApiError && actionError.code.includes("CONFLICT") && group) {
                    try {
                        await refreshGroup(group.id);
                    } catch {
                        // The conflict message stays on screen when the refresh itself fails.
                    }
                }
            } finally {
                setBusyDrafts(current => {
                    const next = { ...current };
                    delete next[draftId];
                    return next;
                });
            }
        },
        [group, refreshGroup]
    );

    const waitForDraftTerminalState = useCallback(
        (draft: DocumentationDraft, token: string | undefined, signal: AbortSignal) =>
            pollDocumentationDraftUntilTerminal({
                draftId: draft.id,
                loadGroup: requestSignal => getDocumentationGroup(draft.documentation_group_id, token, requestSignal),
                onGroupLoaded: response => {
                    setGroup(response.group);
                    setDrafts(current => mergeDocumentationDrafts(current, response.drafts));
                },
                signal
            }),
        []
    );

    const processOne = useCallback(
        (draft: DocumentationDraft, token: string | undefined, followUp?: string) =>
            runWithProcessingController(processingControllersRef.current, async (signal, onActivity) => {
                const events = await processDocumentationDraft(draft, token, followUp, undefined, signal, onActivity);
                if (signal.aborted) throw signal.reason;

                const updated = latestDraft(events);
                if (updated) updateDraft(updated);
                if (shouldPollDocumentationDraft(events, updated)) {
                    await waitForDraftTerminalState(draft, token, signal);
                }
            }),
        [updateDraft, waitForDraftTerminalState]
    );

    const waitForAlreadyProcessingDraft = useCallback(
        (draft: DocumentationDraft, token: string | undefined) =>
            runWithProcessingController(processingControllersRef.current, signal => waitForDraftTerminalState(draft, token, signal)),
        [waitForDraftTerminalState]
    );

    /**
     * Redo the notes already on screen from the same employee input plus one more
     * instruction, instead of abandoning them for a new group. One note is rarely
     * right on the first pass, and a Draft keeps its identity, its edits, and its
     * approval history across a revision.
     */
    const handleRevise = useCallback(
        async (request: string) => {
            const trimmed = request.trim();
            if (!trimmed || revisableDrafts.length === 0) return;
            flowGenerationRef.current += 1;
            cardRequestsRef.current.clear();
            setBusyDrafts({});
            setPendingConversationMessage(trimmed);
            try {
                const token = await resolveToken();
                // Sequential: each turn is a full generation, and the drafts share one
                // request quota.
                for (const draft of revisableDrafts) {
                    await withBusyDraft(draft.id, "revise", async () => {
                        await processOne(draft, token, trimmed);
                    });
                }
                setPendingConversationMessage(null);
                if (group) await refreshGroup(group.id);
            } finally {
                setPendingConversationMessage(null);
            }
        },
        [group, processOne, refreshGroup, resolveToken, revisableDrafts, withBusyDraft]
    );

    const handleCreate = useCallback(
        async (rawText: string) => {
            const trimmedText = rawText.trim();
            if (!trimmedText || selectedProjects.length === 0 || createInFlightRef.current) return;
            // State updates do not become visible until the next render. A ref is
            // the synchronous guard that prevents a second send in that window.
            createInFlightRef.current = true;
            flowGenerationRef.current += 1;
            cardRequestsRef.current.clear();
            setBusyDrafts({});

            setSubmittedText(trimmedText);
            setError(undefined);
            setLastFailedAction(null);
            setDraftErrors({});
            setFieldEdits({});
            setFieldConfirmations({});
            setNodeEdits({});
            setSelectedValues({});
            setNodeConfirmations({});
            setWorkflowSelections({});
            setPendingConversationMessage(null);
            setEditingDraftId(null);
            setEditSnapshot(null);
            setGroup(null);
            setDrafts([]);
            setStage("generating");

            const createController = new AbortController();
            processingControllersRef.current.add(createController);
            const createTimeout = globalThis.setTimeout(() => {
                abortProcessingControllers(processingControllersRef.current, documentationProcessTimeoutError());
            }, DOCUMENTATION_PROCESS_TIMEOUT_MS);
            try {
                const token = await resolveToken();
                if (createController.signal.aborted) throw createController.signal.reason;
                const fingerprint = JSON.stringify({
                    projectIds: selectedProjects.map(project => project.id),
                    rawText: trimmedText,
                    language: i18n.language,
                    routedWorkflowId
                });
                let idempotencyKey: string = crypto.randomUUID();
                const pending = sessionStorage.getItem(PENDING_CREATE_KEY);
                if (pending) {
                    try {
                        const parsed = JSON.parse(pending) as { fingerprint?: string; idempotencyKey?: string };
                        if (parsed.fingerprint === fingerprint && parsed.idempotencyKey) idempotencyKey = parsed.idempotencyKey;
                    } catch {
                        sessionStorage.removeItem(PENDING_CREATE_KEY);
                    }
                }
                sessionStorage.setItem(PENDING_CREATE_KEY, JSON.stringify({ fingerprint, idempotencyKey }));
                const response = await createDocumentationGroup(
                    selectedProjects.map(project => project.id),
                    trimmedText,
                    i18n.language,
                    token,
                    idempotencyKey,
                    createController.signal,
                    routedWorkflowId
                );
                if (createController.signal.aborted) throw createController.signal.reason;
                setGroup(response.group);
                setDrafts(current => mergeDocumentationDrafts(current, response.drafts));
                const processable = response.drafts.filter(draft => draft.status === "created");
                const alreadyProcessing = response.drafts.filter(draft => draft.status !== "created" && isDocumentationDraftProcessing(draft.status));
                await Promise.all([
                    ...processable.map(draft => processOne(draft, token)),
                    ...alreadyProcessing.map(draft => waitForAlreadyProcessingDraft(draft, token))
                ]);
                const completed = await getDocumentationGroup(response.group.id, token, createController.signal);
                if (createController.signal.aborted) throw createController.signal.reason;
                setGroup(completed.group);
                setDrafts(current => mergeDocumentationDrafts(current, completed.drafts));
                sessionStorage.removeItem(PENDING_CREATE_KEY);
                setStage("noteReady");
            } catch (createError) {
                abortProcessingControllers(processingControllersRef.current);
                if (
                    createController.signal.reason instanceof DocumentationApiError &&
                    createController.signal.reason.code === "DOCUMENTATION_PROCESS_TIMEOUT"
                ) {
                    setError(createController.signal.reason);
                    setLastFailedAction("generateNote");
                    setStage("error");
                    return;
                }
                if (isAbortError(createError)) return;
                setError(createError);
                setLastFailedAction("generateNote");
                setStage("error");
            } finally {
                globalThis.clearTimeout(createTimeout);
                processingControllersRef.current.delete(createController);
                createInFlightRef.current = false;
            }
        },
        [i18n.language, processOne, resolveToken, selectedProjects, waitForAlreadyProcessingDraft, routedWorkflowId]
    );

    useEffect(() => {
        if (initialMessage && stage === "askWhat" && selectedProjects.length > 0 && !homeMessageStarted.current) {
            homeMessageStarted.current = true;
            void handleCreate(initialMessage);
        }
    }, [initialMessage, stage, selectedProjects, handleCreate]);

    const handleProjectToggled = useCallback((project: Project) => {
        setSelectedProjects(previous =>
            previous.some(item => item.id === project.id) ? previous.filter(item => item.id !== project.id) : [...previous, project]
        );
    }, []);

    const handleConfirmSelection = useCallback(() => {
        if (selectedProjects.length === 0) {
            return;
        }
        setError(undefined);
        setStage("askWhat");
    }, [selectedProjects.length]);

    const handleProjectChipsWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
        const container = projectChipsRowRef.current;
        if (!container) {
            return;
        }

        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
            return;
        }

        const canScrollHorizontally = container.scrollWidth > container.clientWidth;
        if (!canScrollHorizontally) {
            return;
        }

        event.preventDefault();
        container.scrollLeft += event.deltaY;
    }, []);

    /** Persists the reviewer's inline edits, so the draft the server signs is the draft on screen. */
    const persistEdits = useCallback(
        async (draft: DocumentationDraft, token: string | undefined): Promise<DocumentationDraft> => {
            let updated = draft;
            const nodeChanges = buildWorkflowNodeChanges(draft.workflow_node_results, nodeEdits[draft.id] || {});
            const choices = selectedValues[draft.id] || {};
            const confirmedNodes = [...(nodeConfirmations[draft.id] || new Set<string>())];
            if (Object.keys(nodeChanges).length || Object.keys(choices).length || confirmedNodes.length) {
                updated = await updateDocumentationWorkflowNodes(updated, nodeChanges, confirmedNodes, token, choices);
                updateDraft(updated);
                setNodeEdits(current => ({ ...current, [draft.id]: {} }));
                setSelectedValues(current => ({ ...current, [draft.id]: {} }));
                setNodeConfirmations(current => ({ ...current, [draft.id]: new Set<string>() }));
            }

            const changes = fieldEdits[draft.id] || {};
            const confirmedFields = [...(fieldConfirmations[draft.id] || new Set<string>())];
            if (Object.keys(changes).length || confirmedFields.length) {
                updated = await updateDocumentationFields(updated, changes, confirmedFields, token);
                updateDraft(updated);
                setFieldEdits(current => ({ ...current, [draft.id]: {} }));
                setFieldConfirmations(current => ({ ...current, [draft.id]: new Set<string>() }));
            }

            return updated;
        },
        [fieldConfirmations, fieldEdits, nodeConfirmations, nodeEdits, selectedValues, updateDraft]
    );

    // "Save Note": persist the edits and create the unsigned note. Nothing is signed here.
    const handleSaveNote = useCallback(
        (draft: DocumentationDraft) => {
            void withBusyDraft(draft.id, "save", async () => {
                const token = await resolveToken();
                const saved = await persistEdits(draft, token);
                if (saved.status === "ready_for_review") {
                    await approveDocumentationDraft(saved, token, `approve-${saved.id}`);
                }
                if (group) await refreshGroup(group.id);
            });
        },
        [group, persistEdits, refreshGroup, resolveToken, withBusyDraft]
    );



    const handleRetryDraft = useCallback(
        (draft: DocumentationDraft) => {
            void withBusyDraft(draft.id, "retry", async () => {
                const token = await resolveToken();
                if (draft.status === "processing_failed") {
                    await processOne(draft, token);
                } else {
                    await approveDocumentationDraft(draft, token, `approve-${draft.id}`);
                }
                if (group) await refreshGroup(group.id);
            });
        },
        [group, processOne, refreshGroup, resolveToken, withBusyDraft]
    );

    const handleCancelDraft = useCallback(
        (draft: DocumentationDraft) => {
            void withBusyDraft(draft.id, "cancel", async () => {
                updateDraft(await cancelDocumentationDraft(draft, await resolveToken()));
                if (group) await refreshGroup(group.id);
            });
        },
        [group, refreshGroup, resolveToken, updateDraft, withBusyDraft]
    );

    const handleStartEditing = useCallback(
        (draft: DocumentationDraft) => {
            setEditSnapshot({
                draftId: draft.id,
                workflowId: workflowSelections[draft.id] || draft.selected_workflow?.workflow_id || "",
                fieldEdits: { ...(fieldEdits[draft.id] || {}) },
                fieldConfirmations: new Set(fieldConfirmations[draft.id] || []),
                nodeEdits: { ...(nodeEdits[draft.id] || {}) },
                selectedValues: { ...(selectedValues[draft.id] || {}) },
                nodeConfirmations: new Set(nodeConfirmations[draft.id] || [])
            });
            setEditingDraftId(draft.id);
        },
        [fieldConfirmations, fieldEdits, nodeConfirmations, nodeEdits, selectedValues, workflowSelections]
    );

    const handleWorkflowSelection = useCallback((draft: DocumentationDraft, workflowId: string) => {
        setWorkflowSelections(current => ({ ...current, [draft.id]: workflowId }));
    }, []);

    const handleSaveEdit = useCallback(
        (draft: DocumentationDraft) => {
            const selectedWorkflowId = workflowSelections[draft.id] || draft.selected_workflow?.workflow_id || "";
            const workflowChanged = Boolean(selectedWorkflowId && selectedWorkflowId !== draft.selected_workflow?.workflow_id);
            const hasPendingEdit = Boolean(
                workflowChanged ||
                Object.keys(nodeEdits[draft.id] || {}).length ||
                Object.keys(selectedValues[draft.id] || {}).length ||
                nodeConfirmations[draft.id]?.size ||
                Object.keys(fieldEdits[draft.id] || {}).length ||
                fieldConfirmations[draft.id]?.size
            );
            if (!hasPendingEdit) {
                setEditingDraftId(null);
                setEditSnapshot(null);
                return;
            }

            void withBusyDraft(draft.id, "save", async () => {
                const token = await resolveToken();
                if (workflowChanged) {
                    const replaced = await replaceDocumentationWorkflow(draft, selectedWorkflowId, token);
                    updateDraft(replaced);
                    setFieldEdits(current => ({ ...current, [draft.id]: {} }));
                    setFieldConfirmations(current => ({ ...current, [draft.id]: new Set<string>() }));
                    setNodeEdits(current => ({ ...current, [draft.id]: {} }));
                    setSelectedValues(current => ({ ...current, [draft.id]: {} }));
                    setNodeConfirmations(current => ({ ...current, [draft.id]: new Set<string>() }));
                    setWorkflowSelections(current => ({ ...current, [draft.id]: selectedWorkflowId }));
                    setEditingDraftId(null);
                    setEditSnapshot(null);
                    if (group) await refreshGroup(group.id);
                    return;
                }
                const saved = await persistEdits(draft, token);
                setEditingDraftId(null);
                setEditSnapshot(null);
                if (saved.status === "ready_for_review") {
                    await approveDocumentationDraft(saved, token, `approve-${saved.id}`);
                }
                if (group) await refreshGroup(group.id);
            });
        },
        [
            fieldConfirmations,
            fieldEdits,
            group,
            nodeConfirmations,
            nodeEdits,
            selectedValues,
            persistEdits,
            refreshGroup,
            resolveToken,
            updateDraft,
            workflowSelections,
            withBusyDraft
        ]
    );

    const handleCancelEdit = useCallback(() => {
        if (editSnapshot) {
            const snapshot = editSnapshot;
            setFieldEdits(current => ({ ...current, [snapshot.draftId]: snapshot.fieldEdits }));
            setFieldConfirmations(current => ({ ...current, [snapshot.draftId]: snapshot.fieldConfirmations }));
            setNodeEdits(current => ({ ...current, [snapshot.draftId]: snapshot.nodeEdits }));
            setSelectedValues(current => ({ ...current, [snapshot.draftId]: snapshot.selectedValues }));
            setNodeConfirmations(current => ({ ...current, [snapshot.draftId]: snapshot.nodeConfirmations }));
            setWorkflowSelections(current => ({ ...current, [snapshot.draftId]: snapshot.workflowId }));
        }
        setEditingDraftId(null);
        setEditSnapshot(null);
    }, [editSnapshot]);

    const startNew = useCallback(() => {
        if (hasUnsavedEdits && !window.confirm(t("documentFlow.leaveUnsaved", { defaultValue: "Leave without saving? Unsaved edits will be discarded." }))) return;
        flowGenerationRef.current += 1;
        cardRequestsRef.current.clear();
        setBusyDrafts({});
        abortProcessingControllers(processingControllersRef.current);
        sessionStorage.removeItem(PENDING_CREATE_KEY);
        setGroup(null);
        setDrafts([]);
        setFieldEdits({});
        setFieldConfirmations({});
        setNodeEdits({});
        setSelectedValues({});
        setNodeConfirmations({});
        setWorkflowSelections({});
        setPendingConversationMessage(null);
        setDraftErrors({});
        setEditingDraftId(null);
        setEditSnapshot(null);
        setSubmittedText("");
        setError(undefined);
        setStage(selectedProjects.length > 0 ? "askWhat" : "selectProject");
    }, [selectedProjects.length, hasUnsavedEdits, t]);

    const handleRetry = useCallback(() => {
        setError(undefined);

        if (lastFailedAction === "generateNote" && selectedProjects.length > 0 && submittedText) {
            void handleCreate(submittedText);
            return;
        }

        if (lastFailedAction === "loadProjects") {
            setStage("idle");
            void loadProjects()
                .then(() => {
                    setLastFailedAction(null);
                    setStage("selectProject");
                })
                .catch(loadError => {
                    setError(loadError);
                    setLastFailedAction("loadProjects");
                    setStage("error");
                });
            return;
        }

        setLastFailedAction(null);
        setStage(selectedProjects.length > 0 ? "askWhat" : "selectProject");
    }, [handleCreate, lastFailedAction, loadProjects, selectedProjects.length, submittedText]);

    const renderUserMessage = (message: string) => (
        <div className={flow.userMessageRow}>
            <UserChatMessage message={message} />
        </div>
    );

    const renderProjectSelection = () => (
        <div className={flow.projectSelectionPanel}>
            <div className={flow.aiMessage}>{t("documentFlow.selectProjectLabel")}</div>
            {availableProjects.length > 0 ? (
                <>
                    <div ref={projectChipsRowRef} className={flow.projectChipsRow} onWheel={handleProjectChipsWheel}>
                        {availableProjects.map(project => {
                            const isSelected = selectedProjects.some(item => item.id === project.id);

                            return (
                                <button
                                    key={project.id}
                                    type="button"
                                    className={`${flow.projectChip} ${isSelected ? flow.selected : ""}`}
                                    aria-pressed={isSelected}
                                    onClick={() => handleProjectToggled(project)}
                                    disabled={stage !== "selectProject"}
                                >
                                    {project.name}
                                </button>
                            );
                        })}
                    </div>
                    {selectedProjects.length > 0 && (
                        <div className={flow.confirmSelectionRow}>
                            <Button variant="primary" size="sm" onClick={handleConfirmSelection} disabled={stage !== "selectProject"}>
                                {t("documentFlow.confirmSelection")}
                            </Button>
                        </div>
                    )}
                </>
            ) : (
                <div className={flow.aiMessage}>{t("documentFlow.noProjects")}</div>
            )}
        </div>
    );

    const renderNoteCard = (draft: DocumentationDraft) => {
        const busyAction = busyDrafts[draft.id];
        const busy = Boolean(busyAction);
        const isSaving = busyAction === "save";
        const isEditingNote = editingDraftId === draft.id;
        const workflowInstances = draft.workflow_instances || [];
        const hasWorkflowCards = workflowInstances.length > 0;
        const hasMultipleWorkflows = workflowInstances.length > 1;
        const canEdit = !hasMultipleWorkflows && (draft.status === "ready_for_review" || draft.status === "pending_signature");
        // The original kept the note on screen after signing — only its actions went
        // away — so an approved draft still renders its fields, read-only.
        const isApproved = draft.status === "approved";
        const selectedWorkflowId = workflowSelections[draft.id] || draft.selected_workflow?.workflow_id || "";
        const workflowChanged = Boolean(selectedWorkflowId && selectedWorkflowId !== draft.selected_workflow?.workflow_id);
        const showNoteFields =
            draft.status === "ready_for_review" ||
            draft.status === "pending_signature" ||
            isApproved ||
            (hasWorkflowCards && (draft.status === "finalization_failed" || draft.status === "processing_failed"));
        const showActionButtons = showNoteFields && !isApproved && !isEditingNote && !hasWorkflowCards;
        const hasPendingChanges = Boolean(
            Object.keys(nodeEdits[draft.id] || {}).length ||
            Object.keys(selectedValues[draft.id] || {}).length ||
            nodeConfirmations[draft.id]?.size ||
            Object.keys(fieldEdits[draft.id] || {}).length ||
            fieldConfirmations[draft.id]?.size
        );
        const saveSuccess = draft.status === "pending_signature" && !hasPendingChanges;
        const hasEmptyNodeEdit = Object.values(nodeEdits[draft.id] || {}).some(content => content.trim().length === 0);
        // The classifier is allowed to find nothing that fits. That is an outcome, not a
        // failure: the draft stops before generation and the employee describes the event
        // again or starts over, so it explains itself instead of showing a processing error.
        const noWorkflowMatch = draft.status === "processing_failed" && draft.processing?.error_code === "CLASSIFICATION_NO_MATCH";
        // Field-level issues render at their fields; issues without a field need
        // their own box or they would vanish with the old summary bar.
        const globalIssues = (draft.validation?.issues || []).filter(issue => !issue.field_key);
        const draftError = draftErrors[draft.id];
        const renderValidationSummary = (issues: DocumentationValidationIssue[]) =>
            issues.length ? (
                <div className={styles.validationSummary}>
                    <div>
                        {issues.map(issue => (
                            <p key={`${issue.rule_key}-${issue.code}`} className={issue.severity === "error" ? styles.fieldError : styles.fieldWarning}>
                                {issue.message}
                            </p>
                        ))}
                    </div>
                </div>
            ) : null;

        return (
            <div className={`${flow.noteContentWrapper} ${styles.noteCard}`} key={draft.id} aria-busy={busy}>
                {drafts.length > 1 ? (
                    <div className={styles.draftHeader}>
                        <span className={styles.draftProject}>{projectNames.get(draft.project_id) || draft.project_id}</span>
                    </div>
                ) : null}

                {draft.status === "processing" || draft.status === "created" || draft.status === "finalizing" ? <AnswerLoading /> : null}

                {noWorkflowMatch ? (
                    <div className={styles.infoBox}>
                        <AlertCircle size={18} />
                        <span>{t("documentFlow.noWorkflowMatch")}</span>
                    </div>
                ) : draft.status === "blocked_no_definition" ? (
                    <div className={styles.errorBox}>{t("documentFlow.noDefinition")}</div>
                ) : null}

                {(draft.status === "processing_failed" && !noWorkflowMatch && !hasWorkflowCards) || (draft.status === "finalization_failed" && !hasWorkflowCards) ? (
                    <div className={styles.errorBox}>
                        <div>
                            <p>{processingFailure(draft.processing?.error_code) ?? t("documentFlow.processingFailed")}</p>
                            {(draft.status === "processing_failed" && draft.processing?.retryable === false) ||
                            (draft.status === "finalization_failed" && draft.finalization?.retryable === false) ? (
                                <p>{t(draft.status === "processing_failed" ? "documentFlow.configurationFailed" : "documentFlow.manualResolutionRequired")}</p>
                            ) : (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    leftIcon={<RotateCcw size={16} />}
                                    isLoading={busy}
                                    disabled={busy}
                                    onClick={() => handleRetryDraft(draft)}
                                >
                                    {t("documentFlow.retry")}
                                </Button>
                            )}
                        </div>
                    </div>
                ) : null}

                {showNoteFields ? (
                    hasWorkflowCards ? (
                        <WorkflowCardsReview
                            draft={draft}
                            busy={busy}
                            onDirtyChange={handleCardDirty}
                            onSubmit={instanceId => runCardAction(draft, token => submitDocumentationCard(draft, instanceId, token))}
                            onSourceNoteClick={(noteId, sourceProjectId) => void openSourceNote(sourceProjectId ?? draft.project_id, noteId)}
                            onRetryGeneration={async instanceId => {
                                setBusyDrafts(current => ({ ...current, [draft.id]: "retry" }));
                                try {
                                    const events = await processDocumentationDraft(draft, await resolveToken(), undefined, instanceId);
                                    const updated = latestDraft(events);
                                    if (updated) updateDraft(updated);
                                } finally {
                                    try {
                                        if (group) await refreshGroup(group.id);
                                    } finally {
                                        setBusyDrafts(current => {
                                            const next = { ...current };
                                            delete next[draft.id];
                                            return next;
                                        });
                                    }
                                }
                            }}
                            onReplaceWorkflow={(instanceId, workflowId) => runCardAction(draft, token => replaceDocumentationCardWorkflow(draft, instanceId, workflowId, token))}
                            onSave={(instanceId, changes) => runCardAction(draft, token => saveDocumentationCard(draft, instanceId, changes, token))}
                        />
                    ) : isEditingNote ? (
                        <div className={flow.editContainer}>
                            <DocumentationCardEditor
                                draft={draft}
                                selectedWorkflowId={selectedWorkflowId}
                                fieldEdits={fieldEdits[draft.id] || {}}
                                fieldConfirmations={fieldConfirmations[draft.id] || new Set<string>()}
                                nodeEdits={nodeEdits[draft.id] || {}}
                                selectedValues={selectedValues[draft.id] || {}}
                                onChoiceEdit={(nodeId, value, content) => {
                                    setSelectedValues(current => ({ ...current, [draft.id]: { ...(current[draft.id] || {}), [nodeId]: value } }));
                                    setNodeEdits(current => ({ ...current, [draft.id]: { ...(current[draft.id] || {}), [nodeId]: content } }));
                                }}
                                nodeConfirmations={nodeConfirmations[draft.id] || new Set<string>()}
                                disabled={busy}
                                onWorkflowSelection={workflowId => handleWorkflowSelection(draft, workflowId)}
                                onSourceNoteClick={(noteId, sourceProjectId) => void openSourceNote(sourceProjectId ?? draft.project_id, noteId)}
                                onFieldEdit={(fieldKey, value) =>
                                    setFieldEdits(current => ({
                                        ...current,
                                        [draft.id]: { ...(current[draft.id] || {}), [fieldKey]: value }
                                    }))
                                }
                                onFieldConfirmation={(fieldKey, confirmed) =>
                                    setFieldConfirmations(current => {
                                        const next = new Set(current[draft.id] || []);
                                        if (confirmed) next.add(fieldKey);
                                        else next.delete(fieldKey);
                                        return { ...current, [draft.id]: next };
                                    })
                                }
                                onNodeEdit={(nodeId, content) =>
                                    setNodeEdits(current => ({
                                        ...current,
                                        [draft.id]: { ...(current[draft.id] || {}), [nodeId]: content }
                                    }))
                                }
                                onNodeConfirmation={(nodeId, confirmed) =>
                                    setNodeConfirmations(current => {
                                        const next = new Set(current[draft.id] || []);
                                        if (confirmed) next.add(nodeId);
                                        else next.delete(nodeId);
                                        return { ...current, [draft.id]: next };
                                    })
                                }
                            />
                            <div className={flow.editActions}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    iconOnly
                                    iconShape="square"
                                    onClick={() => handleSaveEdit(draft)}
                                    isLoading={isSaving}
                                    disabled={busy || (!workflowChanged && hasEmptyNodeEdit)}
                                    title={t("tooltips.save")}
                                >
                                    <Check size={18} />
                                </Button>
                                <Button variant="ghost" size="sm" iconOnly iconShape="square" onClick={handleCancelEdit} title={t("tooltips.cancel")}>
                                    <X size={18} />
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            {hasMultipleWorkflows ? (
                                <WorkflowInstanceSwitcher
                                    header={<DocumentationNoteDateTime {...stockholmDateTime(draft.created_at)} />}
                                    tabs={workflowInstances.map(instance => ({
                                        id: instance.id,
                                        title: instance.workflow_title,
                                        content: (
                                            <section className={styles.workflowInstance}>
                                                <header className={styles.workflowInstanceHeader}>
                                                    <h2>{instance.workflow_title}</h2>
                                                </header>
                                                {instance.selection_reason ? <p className={styles.workflowMatchReason}>{instance.selection_reason}</p> : null}
                                                {instance.status === "failed" ? (
                                                    <div className={styles.errorBox}>
                                                        <AlertCircle size={18} />
                                                        <span title={instance.error?.code}>{t("documentFlow.processingFailed")}</span>
                                                    </div>
                                                ) : (
                                                    <WorkflowNodeResults
                                                        goals={instance.context_options?.active_goals}
                                                        nodes={instance.workflow_node_results}
                                                        edits={{}}
                                                        confirmations={new Set<string>()}
                                                        sourceNotes={instance.context_options?.signed_notes}
                                                        disabled
                                                        readOnly
                                                        onSourceNoteClick={(noteId, sourceProjectId) => void openSourceNote(sourceProjectId ?? draft.project_id, noteId)}
                                                        onEdit={() => undefined}
                                                        onConfirmation={() => undefined}
                                                    />
                                                )}
                                                {renderValidationSummary((instance.validation?.issues || []).filter(issue => !issue.field_key))}
                                            </section>
                                        )
                                    }))}
                                />
                            ) : (
                                <>
                                    <DocumentationNoteDateTime {...stockholmDateTime(draft.created_at)} />
                                    <WorkflowNodeResults
                                        goals={draft.context_options?.active_goals}
                                        nodes={draft.workflow_node_results}
                                        edits={nodeEdits[draft.id] || {}}
                                        selectedValues={selectedValues[draft.id] || {}}
                                        confirmations={nodeConfirmations[draft.id] || new Set<string>()}
                                        sourceNotes={draft.context_options?.signed_notes}
                                        disabled
                                        readOnly
                                        onSourceNoteClick={(noteId, sourceProjectId) => void openSourceNote(sourceProjectId ?? draft.project_id, noteId)}
                                        onEdit={() => undefined}
                                        onConfirmation={() => undefined}
                                    />
                                </>
                            )}
                        </div>
                    )
                ) : null}

                {showNoteFields && !isEditingNote && !hasMultipleWorkflows ? renderValidationSummary(globalIssues) : null}

                {draftError ? (
                    <div className={styles.errorBox}>
                        <AlertCircle size={18} />
                        <span>{draftError}</span>
                    </div>
                ) : null}

                {draft.status === "approved" ? (
                    <div className={styles.successBox}>
                        <CheckCircle size={18} /> {t("documentFlow.approved", { noteId: draft.approved_note_id })}
                    </div>
                ) : null}

                {showActionButtons && (
                    <div className={flow.messageActions}>
                        {!hasWorkflowCards && (
                            <>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    leftIcon={<Edit2 size={18} />}
                                    onClick={() => handleStartEditing(draft)}
                                    disabled={busy || !canEdit}
                                >
                                    {t("documentFlow.editNote")}
                                </Button>
                                <Button
                                    variant={saveSuccess ? "success" : "secondary"}
                                    size="sm"
                                    leftIcon={saveSuccess ? <CheckCircle size={18} /> : <Save size={18} />}
                                    isLoading={isSaving}
                                    onClick={() => handleSaveNote(draft)}
                                    disabled={busy || saveSuccess || hasEmptyNodeEdit}
                                >
                                    {isSaving
                                          ? t("documentFlow.saving")
                                          : saveSuccess
                                            ? t("documentFlow.saved")
                                            : t("documentFlow.saveNote")}
                                </Button>
                            </>
                        )}
                        <div className={styles.actionSpacer} />
                        {!hasWorkflowCards && (
                            <Menu>
                                <Menu.Trigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        iconOnly
                                        iconShape="square"
                                        disabled={busy || !canEdit}
                                        title={t("documentFlow.moreActions")}
                                        aria-label={t("documentFlow.moreActions")}
                                    >
                                        <MoreHorizontal size={18} />
                                    </Button>
                                </Menu.Trigger>
                                <Menu.Content align="end">
                                    <Menu.Item danger icon={<Trash2 size={16} />} disabled={busy || !canEdit} onClick={() => setDiscardTarget(draft.id)}>
                                        {t("documentFlow.cancelDraft")}
                                    </Menu.Item>
                                </Menu.Content>
                            </Menu>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const shouldShowProjectSelection = (!projectId || embedded) && (stage === "idle" || stage === "selectProject");
    // The bar stays mounted even before a project is confirmed (sending is merely
    // disabled) so the chat surface never loses its input. It still unmounts during
    // an inline note edit: the wrapper's "Start new documentation" switch would
    // otherwise discard the note being edited.
    const canSendFromInput = selectedProjects.length > 0 && stage !== "selectProject" && editingDraftId === null && (group === null || revisableDrafts.length > 0);
    const shouldShowBottomInput = editingDraftId === null;
    // With notes already on screen the input continues the conversation about them.
    // It only starts a fresh group when there is nothing left it could revise —
    // "Start new documentation" is the deliberate way to abandon a note.
    const inputRevises = group !== null && revisableDrafts.length > 0;
    const showConversation = stage === "generating" || stage === "noteReady" || stage === "error";
    // While clarification is unresolved the existing card must precede the
    // question. Once a turn produces a new card, the card follows the turns that
    // caused it, so the replacement is visible at the natural end of the timeline.
    const conversationPrecedesCards = shouldRenderConversationBeforeCards(conversationTurns);
    const renderedCards = group && stage !== "generating" ? drafts.map(renderNoteCard) : null;

    const bottomInput = (
        <div className={embedded ? undefined : flow.chatInput}>
            {group ? (
                <div className={styles.composerMode}>
                    <span className={styles.composerModeChip}>
                        <Edit2 size={12} />
                        {inputRevises ? t("documentFlow.revisingChip") : t("documentFlow.submittedComposer", { defaultValue: "Cards have been submitted. Edit remaining drafts directly, or start a new note." })}
                    </span>
                    <button type="button" className={styles.composerModeSwitch} onClick={startNew} disabled={isBusy}>
                        {t("documentFlow.newNoteChip")} ›
                    </button>
                </div>
            ) : null}
            <QuestionInput
                clearOnSend
                placeholder={t(inputRevises ? "documentFlow.revisePlaceholder" : "documentFlow.inputPlaceholder")}
                disabled={!canSendFromInput || stage === "generating" || isBusy || pendingConversationMessage !== null}
                onSend={(question, _isWebSearch) => void (inputRevises ? handleRevise(question) : handleCreate(question))}
                showSpeechInput={canSendFromInput}
                autoStartSpeechInputToken={canSendFromInput ? pendingVoiceToken : undefined}
                onSpeechInputAutoStarted={() => setPendingVoiceToken(undefined)}
            />
        </div>
    );

    return (
        <>
            <div className={embedded ? styles.inlineContainer : flow.container}>
                <div className={embedded ? styles.inlineContent : flow.content}>
                    {!embedded && <div className={styles.flowHeader}>
                        <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            iconShape="square"
                            onClick={() => {
                                if (!hasUnsavedEdits || window.confirm(t("documentFlow.leaveUnsaved", { defaultValue: "Leave without saving? Unsaved edits will be discarded." }))) onDismiss();
                            }}
                            title={t("documentFlow.closeFlow")}
                            aria-label={t("documentFlow.closeFlow")}
                        >
                            <ChevronLeft size={18} />
                        </Button>
                        <span className={styles.flowTitle}>
                            {t("documentFlow.userLabel")}
                            {selectedProjectNames ? ` · ${selectedProjectNames}` : ""}
                        </span>
                    </div>}

                    {!embedded && renderUserMessage(t("documentFlow.userLabel"))}

                    {shouldShowProjectSelection && renderProjectSelection()}

                    {selectedProjects.length > 0 && stage !== "selectProject" && (
                        <>
                            {!projectId && renderUserMessage(selectedProjectNames)}
                            {!initialMessage && <div className={flow.aiMessage}>{t("documentFlow.whatToDocument", { name: selectedProjectNames })}</div>}
                        </>
                    )}

                    {stage === "idle" && (
                        <div className={flow.noteContentWrapper}>
                            <AnswerLoading />
                        </div>
                    )}

                    {showConversation && submittedText && (!embedded || submittedText !== initialMessage) && renderUserMessage(submittedText)}

                    {stage === "generating" && (
                        <div className={flow.noteContentWrapper}>
                            <AnswerLoading />
                        </div>
                    )}

                    {!conversationPrecedesCards ? renderedCards : null}

                    {showConversation &&
                        conversationTurns.map((turn, index) => (
                            <Fragment key={`conversation-${index}-${turn.created_at}`}>
                                {turn.role === "user" ? renderUserMessage(turn.content) : <div className={flow.aiMessage}>{turn.content}</div>}
                            </Fragment>
                        ))}

                    {conversationPrecedesCards ? renderedCards : null}

                    {showConversation && pendingConversationMessage ? (
                        <>
                            {renderUserMessage(pendingConversationMessage)}
                            <div className={flow.noteContentWrapper}>
                                <AnswerLoading />
                            </div>
                        </>
                    ) : null}

                    {stage === "error" && (
                        <div className={flow.noteContentWrapper}>
                            <AnswerError error={String(error)} onRetry={handleRetry} />
                        </div>
                    )}
                </div>

                {shouldShowBottomInput && (embedded ? (composerTarget ? createPortal(bottomInput, composerTarget) : null) : bottomInput)}
            </div>



            <Modal open={sourceNoteTarget !== null} onOpenChange={open => !open && closeSourceNote()} size="lg">
                <Modal.Header title={sourceNote?.title || t("notes.incident.sources")} onClose={closeSourceNote} />
                <Modal.Body>
                    {sourceNoteError && sourceNoteTarget ? (
                        <AnswerError error={sourceNoteError} onRetry={() => void openSourceNote(sourceNoteTarget.projectId, sourceNoteTarget.noteId)} />
                    ) : sourceNote ? (
                        <InteractiveNoteContent content={sourceNoteContentText(sourceNote.content)} isEditable={false} onContentChange={() => undefined} />
                    ) : (
                        <AnswerLoading />
                    )}
                </Modal.Body>
            </Modal>

            <Modal open={discardTarget !== null} onOpenChange={open => !open && setDiscardTarget(null)} size="sm">
                <Modal.Header
                    title={t("documentFlow.discardConfirmTitle")}
                    description={t("documentFlow.discardConfirmBody")}
                    onClose={() => setDiscardTarget(null)}
                />
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDiscardTarget(null)}>
                        {t("documentFlow.keepDraft")}
                    </Button>
                    <Button
                        variant="danger"
                        leftIcon={<Trash2 size={16} />}
                        onClick={() => {
                            const target = discardTarget;
                            setDiscardTarget(null);
                            const draft = drafts.find(item => item.id === target);
                            if (draft) handleCancelDraft(draft);
                        }}
                    >
                        {t("documentFlow.discardConfirm")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
